import * as angular from 'angular';
import { MarketAnalsis } from "@models/cmodel/marketAnalysis";
import { AnalysisDetails } from "@models/cmodel/analysisDetails";
import * as moment from "moment";
import { CModelRepo } from "@services/cmodel.repo";
import { ReportsRepo } from "@services/reports.repo";
import { CustodianReport } from "@models/portfolioadmin/reportManagement/custodianReport";
import { TableColumn } from "@models/components/tableColumn";
import { extractErrorMessage } from "@common/utils/errorUtils";

class reportSummaryController implements angular.IOnInit {
  public isLoading: boolean = false;
  public marketAnalyses: MarketAnalsis[] = [];

  public selectMarketAnalysis: MarketAnalsis;
  public selectRevision: AnalysisDetails;

  private backUpRevisionText: string;

  // Bulk approval properties
  public activeTab: number = 0; // 0 = market analysis, 1 = bulk approval
  public custodianReportsForApproval: CustodianReport[] = [];
  public filteredReportsForApproval: CustodianReport[] = [];
  public isLoadingApprovalReports: boolean = false;
  public selectedQuarter: number = 1;
  public selectedYear: number = new Date().getFullYear();
  public selectedApprovalStatus: string = "all";
  public isApproving: boolean = false;
  
  public quarterOptions = [
    { value: 1, label: "First Quarter" },
    { value: 2, label: "Second Quarter" },
    { value: 3, label: "Third Quarter" },
    { value: 4, label: "Fourth Quarter" }
  ];

  public yearOptions: number[] = [];
  public approvalStatusOptions = [
    { value: "all", label: "All" },
    { value: "approved", label: "Approved" },
    { value: "unapproved", label: "Unapproved" }
  ];

  public approvalTableColumns: TableColumn[] = [];

  /** @ngInject */
  constructor(
    private cModelRepo: CModelRepo,
    private reportsRepo: ReportsRepo,
    private $mdDialog: angular.material.IDialogService,
    private $scope: angular.IScope,
    private notifyjs: any,
    private $element: angular.IAugmentedJQuery,
    private $timeout: angular.ITimeoutService,
    private $q: angular.IQService
  ) {
    // Generate year options (current year and previous 5 years)
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 6; i++) {
      this.yearOptions.push(currentYear - i);
    }
  }

  $onInit(): void {
    this.isLoading = true;
    this.getReportSummaries();
    this.initializeApprovalTableColumns();
  }

  initializeApprovalTableColumns(): void {
    this.approvalTableColumns = [
      { key: "name", label: "Report Name", type: "text" },
      { key: "reportDate", label: "Report Date", type: "text" },
      { key: "accountNumber", label: "Portfolio/Account Number", type: "text" },
      { key: "approved", label: "Approved", type: "boolean" },
      { key: "createdByName", label: "Created By", type: "text" },
      { key: "approvedByName", label: "Approved By", type: "text" }
    ];
  }


  public getReportSummaries() {
    this.cModelRepo
      .getReportSummaries()
      .then((analyses) => {
        this.marketAnalyses = analyses;
        this.marketAnalyses.forEach((analyes) => {
          analyes.analysisDetails = analyes.analysisDetails.reverse();
        });
      })
      .catch((err) => this.notifyjs(extractErrorMessage(err)))
      .finally(() => (this.isLoading = false));
  }

  public newAnalystText() {
    const dialogScope = this.$scope.$new() as any;
    dialogScope.invalidDates = this.marketAnalyses.map((m) => new Date(m.date));

    this.$mdDialog
      .show({
        template:
          '<md-dialog layout-padding="5"><select-date-dialog invalid-dates="invalidDates"></select-date-dialog></md-dialog>',
        scope: dialogScope,
      })
      .then((date: Date) => {
        this.selectMarketAnalysis = <MarketAnalsis>{
          quarter: moment(date).quarter(),
          revisions: 1,
          date: date.toISOString(),
          analysisDetails: [
            <AnalysisDetails>{
              revisionNo: 1,
              marketAnalysis: "",
              marketAnalysisFrench: "",
            },
          ],
        };
        this.selectRevision = this.selectMarketAnalysis.analysisDetails[0];
      })
      .catch(() => console.log("Cancelled new dialog"));
  }

  public viewAnalysis(analysis: MarketAnalsis) {
    if (this.backUpRevisionText) {
      // Reset any change that might of happened, between changes
      this.selectMarketAnalysis.analysisDetails[0].marketAnalysis =
        this.backUpRevisionText;
    }
    this.selectMarketAnalysis = analysis;
    this.selectRevision = analysis.analysisDetails[0];
    this.backUpRevisionText = analysis.analysisDetails[0].marketAnalysis;
  }

  public onRevisionChange(revision: AnalysisDetails) {
    if (
      revision.revisionNo ===
      this.selectMarketAnalysis.analysisDetails[0].revisionNo
    ) {
      revision.marketAnalysis = this.backUpRevisionText;
    }
  }

  public formatDate(date) {
    return moment(date).fromNow();
  }

  public onLatestRevision() {
    return (
      this.selectRevision.revisionNo ===
      this.selectMarketAnalysis.analysisDetails[0].revisionNo
    );
  }

  public saveRevision() {
    this.cModelRepo
      .saveReportSummaries(this.selectMarketAnalysis)
      .then((updatedMarketAnalysis) => {
        var currentIndex = this.marketAnalyses.findIndex(
          (m) => m.date === updatedMarketAnalysis.date
        );
        if (currentIndex === -1) {
          this.marketAnalyses.push(updatedMarketAnalysis);
        } else {
          this.marketAnalyses[currentIndex] = updatedMarketAnalysis;
        }
        this.backUpRevisionText = "";
        this.getReportSummaries();
      })
      .catch((err) => this.notifyjs(extractErrorMessage(err)))
      .finally(() => {
        let selectedAnalysisIndex = this.marketAnalyses.findIndex(
          (ma) => ma.date === this.selectMarketAnalysis.date
        );
        if (selectedAnalysisIndex > -1) {
          this.viewAnalysis(this.marketAnalyses[selectedAnalysisIndex]);
        }
      });
  }

  // Bulk approval methods
  loadApprovalReports(): void {
    this.isLoadingApprovalReports = true;
    this.clearApprovalSelection();

    this.reportsRepo
      .getCustodianReportsForApproval(
        this.selectedQuarter,
        this.selectedYear,
        this.selectedApprovalStatus === "all" ? null : this.selectedApprovalStatus === "approved"
      )
      .then((response) => {
        const reports = response.data as CustodianReport[];
        this.custodianReportsForApproval = reports.map((report) => ({
          ...report,
          reportDate: moment(report.reportDate).format("DD-MM-YYYY")
        }));
        this.applyApprovalFilters();
        // Clear selection after data is loaded to ensure button text updates
        this.clearApprovalSelection();
      })
      .catch((error) => {
        this.notifyjs(
          "Failed to load custodian reports: " + (error.data || error.message),
          "error"
        );
        this.custodianReportsForApproval = [];
        this.filteredReportsForApproval = [];
        // Clear selection even on error
        this.clearApprovalSelection();
      })
      .finally(() => {
        this.isLoadingApprovalReports = false;
      });
  }

  applyApprovalFilters(): void {
    let filtered = [...this.custodianReportsForApproval];

    if (this.selectedApprovalStatus === "approved") {
      filtered = filtered.filter((r) => r.approved);
    } else if (this.selectedApprovalStatus === "unapproved") {
      filtered = filtered.filter((r) => !r.approved);
    }

    this.filteredReportsForApproval = filtered;
  }

  searchApprovalReports(): void {
    this.loadApprovalReports();
  }

  onApprovalStatusChanged(): void {
    this.applyApprovalFilters();
  }

  isRowDisabled(item: CustodianReport): boolean {
    return item.isCreator;
  }

  getDisabledRowTooltip(item: CustodianReport): string | null {
    if (!this.isRowDisabled(item)) {
      return null;
    }
    // Return the reason from the item if available, otherwise use a default message
    return item.cannotApproveReason || "Cannot approve reports you created";
  }

  private getApprovalTableComponent(): any {
    const tableElement = this.$element.find('reusable-table');
    if (tableElement.length > 0) {
      const tableScope = angular.element(tableElement).isolateScope() as any;
      if (tableScope && tableScope.$ctrl) {
        return tableScope.$ctrl;
      }
    }
    return null;
  }

  private clearApprovalSelection(): void {
    this.$timeout(() => {
      const tableComponent = this.getApprovalTableComponent();
      if (tableComponent && tableComponent.selectedItems) {
        // Clear all selections
        tableComponent.selectedItems.clear();
      }
    });
  }

  getSelectedApprovalCount(): number {
    const tableComponent = this.getApprovalTableComponent();
    if (tableComponent && tableComponent.selectedItems) {
      return tableComponent.selectedItems.size;
    }
    return 0;
  }

  hasSelectedApprovalItems(): boolean {
    const tableComponent = this.getApprovalTableComponent();
    if (tableComponent && tableComponent.selectedItems) {
      return tableComponent.selectedItems.size > 0;
    }
    return false;
  }

  getSelectedApprovalReportIds(): number[] {
    const tableComponent = this.getApprovalTableComponent();
    if (!tableComponent || !tableComponent.selectedItems) {
      return [];
    }
    return Array.from(tableComponent.selectedItems)
      .filter((item: CustodianReport) => !this.isRowDisabled(item))
      .map((item: CustodianReport) => item.reportId);
  }

  getSelectedApprovalReports(): CustodianReport[] {
    const tableComponent = this.getApprovalTableComponent();
    if (!tableComponent || !tableComponent.selectedItems) {
      return [];
    }
    return Array.from(tableComponent.selectedItems)
      .filter((item: CustodianReport) => !this.isRowDisabled(item)) as CustodianReport[];
  }

  getApprovalButtonText(): string {
    const selectedReports = this.getSelectedApprovalReports();
    if (selectedReports.length === 0) {
      return "Approve Selected";
    }

    const approvedCount = selectedReports.filter(r => r.approved).length;
    const unapprovedCount = selectedReports.filter(r => !r.approved).length;

    if (approvedCount > 0 && unapprovedCount > 0) {
      return `Approve/Unapprove Selected (${selectedReports.length})`;
    } else if (approvedCount > 0) {
      return `Unapprove Selected (${selectedReports.length})`;
    } else {
      return `Approve Selected (${selectedReports.length})`;
    }
  }

  approveSelectedReports(): void {
    const selectedReports = this.getSelectedApprovalReports();

    if (selectedReports.length === 0) {
      this.notifyjs(
        "Please select at least one report that can be approved.",
        "warning"
      );
      return;
    }

    // Determine if we need to approve or unapprove
    // If all selected are approved, unapprove them. If all are unapproved, approve them.
    // If mixed, we'll need to handle each individually
    const allApproved = selectedReports.every(r => r.approved);
    const allUnapproved = selectedReports.every(r => !r.approved);

    if (allApproved) {
      // Unapprove all
      this.performBulkApproval(selectedReports, false);
    } else if (allUnapproved) {
      // Approve all
      this.performBulkApproval(selectedReports, true);
    } else {
      // Mixed selection - approve unapproved ones, unapprove approved ones
      const reportIds = selectedReports.map(r => r.reportId);
      const approveIds = selectedReports.filter(r => !r.approved).map(r => r.reportId);
      const unapproveIds = selectedReports.filter(r => r.approved).map(r => r.reportId);

      // Process both actions
      const promises: angular.IPromise<any>[] = [];
      
      if (approveIds.length > 0) {
        promises.push(this.reportsRepo.bulkApproveCustodianReports(approveIds, true));
      }
      
      if (unapproveIds.length > 0) {
        promises.push(this.reportsRepo.bulkApproveCustodianReports(unapproveIds, false));
      }

      this.isApproving = true;

      this.$q.all(promises)
        .then(() => {
          this.notifyjs(
            `${reportIds.length} report(s) updated successfully`,
            "success"
          );
          this.clearApprovalSelection();
          this.loadApprovalReports();
        })
        .catch((error) => {
          let errorMessage = "Failed to update reports";
          if (error.data) {
            if (error.data.errors) {
              errorMessage = Array.isArray(error.data.errors) ? error.data.errors.join(", ") : error.data.errors;
            } else if (typeof error.data === "string") {
              errorMessage = error.data;
            }
          } else if (error.message) {
            errorMessage = error.message;
          }
          this.notifyjs(errorMessage, "error");
        })
        .finally(() => {
          this.isApproving = false;
        });
    }
  }

  private performBulkApproval(reports: CustodianReport[], approve: boolean): void {
    const reportIds = reports.map(r => r.reportId);
    this.isApproving = true;

    this.reportsRepo
      .bulkApproveCustodianReports(reportIds, approve)
      .then(() => {
        const actionText = approve ? "approved" : "unapproved";
        this.notifyjs(
          `${reportIds.length} report(s) ${actionText} successfully`,
          "success"
        );
        this.clearApprovalSelection();
        this.loadApprovalReports();
      })
      .catch((error) => {
        let errorMessage = `Failed to ${approve ? "approve" : "unapprove"} reports`;
        if (error.data) {
          if (error.data.errors) {
            errorMessage = Array.isArray(error.data.errors) ? error.data.errors.join(", ") : error.data.errors;
          } else if (typeof error.data === "string") {
            errorMessage = error.data;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        this.notifyjs(errorMessage, "error");
      })
      .finally(() => {
        this.isApproving = false;
      });
  }

  onTabChanged(tabIndex: number): void {
    this.activeTab = tabIndex;
    if (tabIndex === 1) {
      // Load approval reports when switching to approval tab
      this.loadApprovalReports();
    }
  }
}

export const reportSummary: angular.IComponentOptions = {
  template: require("./reportSummary.html"),
  controller: reportSummaryController,
};



// WEBPACK FOOTER //
// ./src/app/cmodel/cmodel.marketAnalysis/reportSummary.ts